import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  IconButton,
  Alert,
  LinearProgress,
  Chip
} from '@mui/material';
import {
  Close as CloseIcon,
  CloudUpload as UploadIcon,
  PictureAsPdf as PdfIcon,
  Delete as DeleteIcon
} from '@mui/icons-material';

import API_CONFIG from '../config/api';

const BACKEND_URL = API_CONFIG.BACKEND_URL;

export default function RegulamentoModal({ open, onClose, regulamento, mode }) {
  const [formData, setFormData] = useState({
    nome: '',
    descricao: '',
    tipo: 'Regulamento',
    arquivo: null
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Reset form when modal opens/closes
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && regulamento) {
        setFormData({
          nome: regulamento.nome || '',
          descricao: regulamento.descricao || '',
          tipo: regulamento.tipo || 'Regulamento',
          arquivo: null
        });
      } else {
        setFormData({
          nome: '',
          descricao: '',
          tipo: 'Regulamento',
          arquivo: null
        });
      }
      setError(null);
      setSuccess(null);
      setUploadProgress(0);
    }
  }, [open, mode, regulamento]);

  // Handle form input changes
  const handleInputChange = (field) => (event) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  // Handle file selection
  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type !== 'application/pdf') {
        setError('Apenas arquivos PDF são permitidos');
        return;
      }
      
      if (file.size > 10 * 1024 * 1024) { // 10MB limit
        setError('Arquivo muito grande. Máximo 10MB');
        return;
      }

      setFormData(prev => ({
        ...prev,
        arquivo: file
      }));
      setError(null);
    }
  };

  // Remove selected file
  const handleRemoveFile = () => {
    setFormData(prev => ({
      ...prev,
      arquivo: null
    }));
  };

  // Format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!formData.nome.trim()) {
      setError('Nome do regulamento é obrigatório');
      return;
    }

    if (mode === 'add' && !formData.arquivo) {
      setError('Arquivo PDF é obrigatório');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formDataToSend = new FormData();
      formDataToSend.append('nome', formData.nome.trim());
      formDataToSend.append('descricao', formData.descricao.trim());
      formDataToSend.append('tipo', formData.tipo);
      
      if (formData.arquivo) {
        formDataToSend.append('arquivo', formData.arquivo);
      }

      const url = mode === 'edit' 
        ? `${BACKEND_URL}/api/regulamentos/${regulamento.id}`
        : `${BACKEND_URL}/api/regulamentos`;

      const method = mode === 'edit' ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        body: formDataToSend
      });

      if (response.ok) {
        setSuccess(mode === 'edit' ? 'Regulamento atualizado com sucesso!' : 'Regulamento cadastrado com sucesso!');
        
        // Wait a bit then close modal and refresh
        setTimeout(() => {
          onClose();
          window.dispatchEvent(new CustomEvent('regulamentoSaved'));
        }, 1500);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erro ao salvar regulamento');
      }
    } catch (error) {
      console.error('Erro ao salvar regulamento:', error);
      setError(error.message || 'Erro ao salvar regulamento');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          background: 'linear-gradient(135deg, rgba(255,255,255,0.95) 0%, rgba(240,248,255,0.9) 100%)',
          backdropFilter: 'blur(10px)'
        }
      }}
    >
      <DialogTitle sx={{ 
        background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
        color: 'white',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between'
      }}>
        <Box display="flex" alignItems="center">
          <PdfIcon sx={{ mr: 1 }} />
          <Typography variant="h6" fontWeight={600}>
            {mode === 'edit' ? 'Editar Regulamento' : 'Adicionar Regulamento'}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        {/* Loading Progress */}
        {loading && (
          <Box sx={{ mb: 2 }}>
            <LinearProgress />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              {uploadProgress > 0 ? `Enviando... ${uploadProgress}%` : 'Salvando regulamento...'}
            </Typography>
          </Box>
        )}

        {/* Form Fields */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Nome do Documento */}
          <TextField
            label="Nome do Documento"
            value={formData.nome}
            onChange={handleInputChange('nome')}
            fullWidth
            required
            disabled={loading}
            placeholder="Ex: Regulamento de Compras e Contratações"
            helperText="Nome que aparecerá no card do documento"
          />

          {/* Descrição */}
          <TextField
            label="Descrição"
            value={formData.descricao}
            onChange={handleInputChange('descricao')}
            fullWidth
            multiline
            rows={3}
            disabled={loading}
            placeholder="Breve descrição do documento (opcional)"
            helperText="Descrição que aparecerá no card do documento"
          />

          {/* Tipo */}
          <TextField
            label="Tipo"
            value={formData.tipo}
            onChange={handleInputChange('tipo')}
            fullWidth
            disabled={loading}
            placeholder="Ex: Regulamento, Manual, Norma"
            helperText="Categoria do documento"
          />

          {/* Upload de Arquivo */}
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Arquivo PDF {mode === 'add' && '*'}
            </Typography>
            
            {!formData.arquivo && (
              <Box
                sx={{
                  border: '2px dashed #ccc',
                  borderRadius: 2,
                  p: 3,
                  textAlign: 'center',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: '#1976d2',
                    backgroundColor: 'rgba(25,118,210,0.05)'
                  }
                }}
                onClick={() => document.getElementById('file-input').click()}
              >
                <UploadIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Clique para selecionar um arquivo PDF
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Máximo 10MB
                </Typography>
              </Box>
            )}

            {formData.arquivo && (
              <Box
                sx={{
                  border: '1px solid #e0e0e0',
                  borderRadius: 2,
                  p: 2,
                  backgroundColor: 'rgba(25,118,210,0.05)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between'
                }}
              >
                <Box display="flex" alignItems="center">
                  <PdfIcon sx={{ color: '#d32f2f', mr: 1 }} />
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      {formData.arquivo.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatFileSize(formData.arquivo.size)}
                    </Typography>
                  </Box>
                </Box>
                <IconButton 
                  size="small" 
                  color="error" 
                  onClick={handleRemoveFile}
                  disabled={loading}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            )}

            <input
              id="file-input"
              type="file"
              accept=".pdf"
              onChange={handleFileChange}
              style={{ display: 'none' }}
              disabled={loading}
            />
          </Box>

          {/* Existing file info for edit mode */}
          {mode === 'edit' && regulamento?.arquivo_path && !formData.arquivo && (
            <Box sx={{ mt: 1 }}>
              <Chip
                icon={<PdfIcon />}
                label={`Arquivo atual: ${regulamento.arquivo_path}`}
                color="primary"
                variant="outlined"
                size="small"
              />
              <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                Selecione um novo arquivo para substituir
              </Typography>
            </Box>
          )}
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 0 }}>
        <Button 
          onClick={onClose} 
          disabled={loading}
          sx={{ mr: 1 }}
        >
          Cancelar
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          sx={{
            background: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #1565c0 0%, #1976d2 100%)'
            }
          }}
        >
          {loading ? 'Salvando...' : (mode === 'edit' ? 'Atualizar' : 'Cadastrar')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
